import React, { useEffect, useState } from "react";
import {
    PlusIcon,
    XMarkIcon,
    CheckCircleIcon,
    TrashIcon,
    PencilSquareIcon,
    ExclamationTriangleIcon,
    QuestionMarkCircleIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { useFaqs } from "../hooks/useFaqs";
import type { Faq } from "../services/faqService";

// ─── Types ────────────────────────────────────────────────────────────────────

type FaqForm = {
    category: string;
    question: string;
    answer: string;
    keywords: string;
    sort_order: string;
    is_popular: boolean;
    is_active: boolean;
};

const EMPTY_FORM: FaqForm = {
    category: "",
    question: "",
    answer: "",
    keywords: "",
    sort_order: "0",
    is_popular: false,
    is_active: true,
};

const CATEGORIES = [
    "Orders",
    "Delivery",
    "Payments",
    "Returns & Refunds",
    "Products & Stock",
    "Vouchers & Specials",
    "Account & Login",
    "Contact Support",
];

// ─── Spinner ──────────────────────────────────────────────────────────────────

const Spinner = () => (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FaqsPage() {
    const {
        faqs,
        loading,
        error: faqsError,
        fetchFaqs,
        createFaq,
        updateFaq,
        toggleFaqActive,
        deleteFaq,
    } = useFaqs();

    const [search, setSearch] = useState("");
    const [filterCategory, setFilterCategory] = useState<string>("All");
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(CATEGORIES));

    // Create modal
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState<FaqForm>(EMPTY_FORM);
    const [creating, setCreating] = useState(false);
    const [createSuccess, setCreateSuccess] = useState("");
    const [createError, setCreateError] = useState("");

    // Edit modal
    const [editFaq, setEditFaq] = useState<Faq | null>(null);
    const [editForm, setEditForm] = useState<FaqForm>(EMPTY_FORM);
    const [editing, setEditing] = useState(false);
    const [editSuccess, setEditSuccess] = useState("");
    const [editError, setEditError] = useState("");

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState<Faq | null>(null);
    const [deleting, setDeleting] = useState(false);

    // ── Fetch ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        void fetchFaqs();
    }, [fetchFaqs]);

    // ── Create ───────────────────────────────────────────────────────────────

    const openCreate = (prefillCategory?: string) => {
        setCreateForm({ ...EMPTY_FORM, category: prefillCategory ?? "" });
        setCreateSuccess("");
        setCreateError("");
        setIsCreateOpen(true);
    };

    const submitCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        setCreateError("");

        const payload = {
            category: createForm.category.trim(),
            question: createForm.question.trim(),
            answer: createForm.answer.trim(),
            keywords: createForm.keywords
                .split(",")
                .map((k) => k.trim())
                .filter(Boolean),
            sort_order: parseInt(createForm.sort_order) || 0,
            is_popular: createForm.is_popular,
            is_active: createForm.is_active,
        };

        try {
            const created = await createFaq(payload);
            if (!created) {
                setCreateError(faqsError ?? "Failed to create FAQ.");
                return;
            }

            setCreateSuccess("FAQ created successfully!");

            setTimeout(() => {
                setIsCreateOpen(false);
                setCreateSuccess("");
            }, 1500);
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : "Failed to create FAQ.");
        } finally {
            setCreating(false);
        }
    };

    // ── Edit ─────────────────────────────────────────────────────────────────

    const openEdit = (faq: Faq) => {
        setEditFaq(faq);
        setEditForm({
            category: faq.category,
            question: faq.question,
            answer: faq.answer,
            keywords: (faq.keywords ?? []).join(", "),
            sort_order: String(faq.sort_order),
            is_popular: faq.is_popular,
            is_active: faq.is_active,
        });
        setEditSuccess("");
        setEditError("");
    };

    const submitEdit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!editFaq) return;

        setEditing(true);
        setEditError("");

        const patch = {
            category: editForm.category.trim(),
            question: editForm.question.trim(),
            answer: editForm.answer.trim(),
            keywords: editForm.keywords
                .split(",")
                .map((k) => k.trim())
                .filter(Boolean),
            sort_order: parseInt(editForm.sort_order) || 0,
            is_popular: editForm.is_popular,
            is_active: editForm.is_active,
        };

        try {
            const updated = await updateFaq(editFaq.id, patch);
            if (!updated) {
                setEditError(faqsError ?? "Failed to update FAQ.");
                return;
            }

            setEditSuccess("FAQ updated!");

            setTimeout(() => {
                setEditFaq(null);
                setEditSuccess("");
            }, 1500);
        } catch (err) {
            setEditError(err instanceof Error ? err.message : "Failed to update FAQ.");
        } finally {
            setEditing(false);
        }
    };

    // ── Toggle active ─────────────────────────────────────────────────────────

    const toggleActive = async (faq: Faq) => {
        await toggleFaqActive(faq);
    };

    // ── Delete ────────────────────────────────────────────────────────────────

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        setDeleting(true);

        try {
            const deleted = await deleteFaq(deleteTarget.id);

            if (deleted) {
                setDeleteTarget(null);
            }
        } finally {
            setDeleting(false);
        }
    };

    // ── Filtering ─────────────────────────────────────────────────────────────

    const filtered = faqs.filter((f) => {
        const normalizedSearch = search.toLowerCase();

        const matchesSearch =
            !search ||
            f.question.toLowerCase().includes(normalizedSearch) ||
            f.answer.toLowerCase().includes(normalizedSearch) ||
            f.category.toLowerCase().includes(normalizedSearch) ||
            f.keywords?.some((keyword) => keyword.toLowerCase().includes(normalizedSearch));

        const matchesCat = filterCategory === "All" || f.category === filterCategory;

        return matchesSearch && matchesCat;
    });

    const grouped = CATEGORIES.reduce<Record<string, Faq[]>>((acc, cat) => {
        const items = filtered.filter((f) => f.category === cat);
        if (items.length > 0) acc[cat] = items;
        return acc;
    }, {});

    const toggleCategory = (cat: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat);
            else next.add(cat);
            return next;
        });
    };

    const fieldClass =
        "block w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:ring-2 focus:ring-[#ee2b74] focus:border-[#ee2b74] outline-none transition-all";

    // ── Form renderer ─────────────────────────────────────────────────────────

    const renderForm = (
        form: FaqForm,
        setForm: React.Dispatch<React.SetStateAction<FaqForm>>,
        successMsg: string,
        errorMsg: string
    ) => (
        <div className="mt-5 space-y-4">
            {successMsg && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm border border-green-200">
                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                    {successMsg}
                </div>
            )}

            {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                    {errorMsg}
                </div>
            )}

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Category <span className="text-red-500">*</span>
                </label>
                <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                    className={fieldClass}
                >
                    <option value="">Select a category…</option>
                    {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Question <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={form.question}
                    onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))}
                    placeholder="e.g. How do I track my order?"
                    className={fieldClass}
                />
            </div>

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Answer <span className="text-red-500">*</span>
                </label>
                <textarea
                    required
                    rows={4}
                    value={form.answer}
                    onChange={(e) => setForm((p) => ({ ...p, answer: e.target.value }))}
                    placeholder="Write the answer. Use [link:path|Label] for in-app links."
                    className={`${fieldClass} resize-none`}
                />
                <p className="mt-1 text-xs text-stone-400">
                    Use <code className="bg-stone-100 px-1 rounded">[link:profile/orders|My Orders]</code> syntax for in-app links.
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Keywords <span className="text-stone-400 font-normal">(optional)</span>
                </label>
                <input
                    type="text"
                    value={form.keywords}
                    onChange={(e) => setForm((p) => ({ ...p, keywords: e.target.value }))}
                    placeholder="track, order status, where, delivery"
                    className={fieldClass}
                />
                <p className="mt-1 text-xs text-stone-400">Comma-separated. Used for search matching.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">Sort Order</label>
                    <input
                        type="number"
                        min="0"
                        value={form.sort_order}
                        onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                        className={fieldClass}
                    />
                </div>

                <div className="flex flex-col gap-3 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer mt-5">
                        <input
                            type="checkbox"
                            checked={form.is_popular}
                            onChange={(e) => setForm((p) => ({ ...p, is_popular: e.target.checked }))}
                            className="h-4 w-4 rounded border-stone-300 text-[#ee2b74] focus:ring-[#ee2b74]"
                        />
                        <span className="text-sm text-stone-700 font-medium">Mark as popular</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.is_active}
                            onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                            className="h-4 w-4 rounded border-stone-300 text-[#ee2b74] focus:ring-[#ee2b74]"
                        />
                        <span className="text-sm text-stone-700 font-medium">Active</span>
                    </label>
                </div>
            </div>
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    const allCategories = Object.keys(grouped);

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-semibold text-stone-900">FAQs</h2>
                    <p className="text-sm text-stone-500 mt-1">
                        Manage frequently asked questions shown to customers in the app
                    </p>
                </div>

                <button
                    onClick={() => openCreate()}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d01560] transition-colors whitespace-nowrap"
                >
                    <PlusIcon className="h-4 w-4" />
                    Add FAQ
                </button>
            </div>

            {faqsError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                    {faqsError}
                </div>
            )}

            {/* Search + filter bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                    <input
                        type="text"
                        placeholder="Search questions, answers…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="block w-full rounded-lg border border-stone-300 pl-9 pr-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:ring-2 focus:ring-[#ee2b74] focus:border-[#ee2b74] outline-none transition-all"
                    />
                </div>

                <div className="relative">
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="appearance-none rounded-lg border border-stone-300 pl-3 pr-9 py-2.5 text-sm text-stone-700 focus:ring-2 focus:ring-[#ee2b74] focus:border-[#ee2b74] outline-none transition-all bg-white w-full"
                    >
                        <option value="All">All categories</option>
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-800" />
                </div>
            </div>

            {/* Stats row */}
            {!loading && (
                <div className="flex flex-wrap gap-3 mb-6">
                    {[
                        { label: "Total FAQs", value: faqs.length },
                        { label: "Active", value: faqs.filter((f) => f.is_active).length },
                        { label: "Popular", value: faqs.filter((f) => f.is_popular).length },
                        { label: "Categories", value: CATEGORIES.length },
                    ].map(({ label, value }) => (
                        <div key={label} className="rounded-xl border border-stone-200 bg-white px-4 py-3 flex items-center gap-3 shadow-sm">
                            <span className="text-xl font-bold text-stone-900">{value}</span>
                            <span className="text-xs text-stone-500">{label}</span>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="py-12 text-center text-stone-400 text-sm">Loading FAQs…</div>
            ) : faqs.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-stone-200 bg-white p-12 text-center">
                    <QuestionMarkCircleIcon className="h-10 w-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-stone-600">No FAQs yet</p>
                    <p className="text-xs text-stone-400 mt-1 mb-4">
                        Add your first FAQ to help customers find answers quickly.
                    </p>
                    <button
                        onClick={() => openCreate()}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d01560] transition-colors"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add First FAQ
                    </button>
                </div>
            ) : allCategories.length === 0 ? (
                <div className="py-12 text-center text-stone-400 text-sm">No FAQs match your search.</div>
            ) : (
                <div className="space-y-4">
                    {allCategories.map((cat) => {
                        const items = grouped[cat];
                        const isExpanded = expandedCategories.has(cat);

                        return (
                            <div key={cat} className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                                <div
                                    onClick={() => toggleCategory(cat)}
                                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 transition-colors cursor-pointer"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-lg bg-pink-50 flex items-center justify-center flex-shrink-0">
                                            <QuestionMarkCircleIcon className="h-4 w-4 text-[#ee2b74]" />
                                        </div>
                                        <div className="text-left">
                                            <span className="text-sm font-semibold text-stone-900">{cat}</span>
                                            <span className="ml-2 text-xs text-stone-400">
                                                {items.length} {items.length === 1 ? "entry" : "entries"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openCreate(cat);
                                            }}
                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-stone-200 text-stone-500 text-xs font-medium hover:border-[#ee2b74] hover:text-[#ee2b74] hover:bg-pink-50 transition-colors"
                                        >
                                            <PlusIcon className="h-3 w-3" />
                                            Add
                                        </button>
                                        <ChevronDownIcon className={`h-4 w-4 text-stone-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="divide-y divide-stone-100 border-t border-stone-100">
                                        {items.map((faq) => (
                                            <FaqRow
                                                key={faq.id}
                                                faq={faq}
                                                onEdit={() => openEdit(faq)}
                                                onToggle={() => toggleActive(faq)}
                                                onDelete={() => setDeleteTarget(faq)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {isCreateOpen && (
                <Modal
                    title="Add FAQ"
                    subtitle="This FAQ will be visible to customers in the app"
                    onClose={() => setIsCreateOpen(false)}
                >
                    <form onSubmit={submitCreate}>
                        {renderForm(createForm, setCreateForm, createSuccess, createError)}
                        <ModalFooter
                            onCancel={() => setIsCreateOpen(false)}
                            loading={creating}
                            success={!!createSuccess}
                            submitLabel="Add FAQ"
                        />
                    </form>
                </Modal>
            )}

            {editFaq && (
                <Modal
                    title="Edit FAQ"
                    subtitle={editFaq.question}
                    onClose={() => setEditFaq(null)}
                >
                    <form onSubmit={submitEdit}>
                        {renderForm(editForm, setEditForm, editSuccess, editError)}
                        <ModalFooter
                            onCancel={() => setEditFaq(null)}
                            loading={editing}
                            success={!!editSuccess}
                            submitLabel="Save Changes"
                        />
                    </form>
                </Modal>
            )}

            {deleteTarget && (
                <Modal
                    title="Delete FAQ"
                    subtitle="This cannot be undone"
                    onClose={() => setDeleteTarget(null)}
                >
                    <div className="mt-5">
                        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                            <p className="font-semibold mb-1">Are you sure you want to delete this FAQ?</p>
                            <p className="font-mono text-xs bg-red-100 rounded px-2 py-1 mt-2 inline-block">
                                {deleteTarget.question}
                            </p>
                        </div>
                        <div className="flex justify-end gap-3 pt-5 border-t border-stone-100 mt-5">
                            <button
                                type="button"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
                            >
                                {deleting ? (
                                    <><Spinner /> Deleting…</>
                                ) : (
                                    <><TrashIcon className="h-4 w-4" /> Delete</>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ─── FAQ Row ──────────────────────────────────────────────────────────────────

function FaqRow({
                    faq,
                    onEdit,
                    onToggle,
                    onDelete,
                }: {
    faq: Faq;
    onEdit: () => void;
    onToggle: () => void;
    onDelete: () => void;
}) {
    return (
        <div className={`px-5 py-4 flex items-start gap-4 ${!faq.is_active ? "opacity-50" : ""}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-stone-900">{faq.question}</p>
                    {faq.is_popular && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#ee2b74]/5 text-[#ee2b74]">
                            <StarSolid className="h-2.5 w-2.5" />
                            Popular
                        </span>
                    )}
                    {!faq.is_active && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500">
                            INACTIVE
                        </span>
                    )}
                </div>

                <p className="text-xs text-stone-500 line-clamp-2">{faq.answer}</p>

                {faq.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {faq.keywords.map((kw) => (
                            <span key={kw} className="text-[10px] bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                                {kw}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                    onClick={onEdit}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:text-[#ee2b74] hover:border-pink-200 hover:bg-pink-50 transition-colors"
                    title="Edit"
                >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                </button>

                <button
                    onClick={onToggle}
                    className={`p-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        faq.is_active
                            ? "border-stone-200 text-stone-600 hover:bg-stone-50"
                            : "border-pink-200 text-[#ee2b74] hover:bg-pink-50"
                    }`}
                    title={faq.is_active ? "Deactivate" : "Activate"}
                >
                    {faq.is_active ? "Hide" : "Show"}
                </button>

                <button
                    onClick={onDelete}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors"
                    title="Delete"
                >
                    <TrashIcon className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

function Modal({
                   title,
                   subtitle,
                   onClose,
                   children,
               }: {
    title: string;
    subtitle: string;
    onClose: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
                <div className="h-1 bg-[#ee2b74] w-full flex-shrink-0" />
                <div className="p-6 overflow-y-auto flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <div className="min-w-0 pr-4">
                            <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
                            <p className="text-xs text-stone-500 mt-0.5 truncate">{subtitle}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors flex-shrink-0"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}

// ─── Modal footer ─────────────────────────────────────────────────────────────

function ModalFooter({
                         onCancel,
                         loading,
                         success,
                         submitLabel,
                     }: {
    onCancel: () => void;
    loading: boolean;
    success: boolean;
    submitLabel: string;
}) {
    return (
        <div className="flex justify-end gap-3 pt-5 border-t border-stone-100 mt-5">
            <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
            >
                Cancel
            </button>
            <button
                type="submit"
                disabled={loading || success}
                className="px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d01560] disabled:opacity-60 flex items-center gap-2"
            >
                {loading ? (
                    <><Spinner /> Saving…</>
                ) : success ? (
                    <><CheckCircleIcon className="h-4 w-4" /> Saved!</>
                ) : (
                    submitLabel
                )}
            </button>
        </div>
    );
}